import * as React from "react";
import { Link as RouterLink } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import useTheme from "@material-ui/core/styles/useTheme";

import AttachMoney from "@material-ui/icons/AttachMoney";
import CheckCircle from "@material-ui/icons/CheckCircle";
import EmojiObjects from "@material-ui/icons/EmojiObjects";
import Timer from "@material-ui/icons/Timer";

import { PageWrapper } from "../components/PageWrapper/PageWrapper";
import { Iris, IrisAssessment, IrisEditor } from "../components/Iris";
import { PageHeader } from "../components/PageWrapper/PageHeader";
import { SignupButton } from "../components/SignupButton/SignupButton";
import { Container } from "../components/PageWrapper/Container";
import { ColoredBandContainer } from "../components/PageWrapper/ColoredBandContainer";
import { INFO_EMAIL } from "../utils/Constants";

export const PricingPage = () => {
  return (
    <PageWrapper>
      <Container>
        <PageHeader
          overline="Pricing"
          title="Affordable pricing options"
          subtitle={
            <>
              <Iris /> is 30x more affordable than traditional manuscipt
              assessments, try it out now!
            </>
          }
          isMainHeadline={true}
        />
        <Intro />
      </Container>
      <ColoredBandContainer>
        <Pricing canExpand={false} mainPrice="subscription" />
      </ColoredBandContainer>
      <Container>
        <GetStarted />
      </Container>
    </PageWrapper>
  );
};

const Intro = () => {
  const theme = useTheme();

  return (
    <Box mt={8}>
      <Box mb={8}>
        <PageHeader
          overline="Our Advantage"
          title="Instant, affordable, and objective manuscript assessments "
          align="left"
        />
      </Box>
      <Grid container={true} spacing={4}>
        <Grid item={true} xs={12} md={4}>
          <Box
            height={48}
            width={40}
            marginBottom={2}
            px={0}
            borderBottom={`2px solid ${theme.palette.primary.main}`}
            textAlign="center"
          >
            <AttachMoney color="primary" />
          </Box>
          <Typography
            variant="h5"
            gutterBottom={true}
            style={{ fontWeight: 700 }}
          >
            Affordable
          </Typography>
          <Typography color="textSecondary">
            Traditional manuscript assessments cost ~$0.01 - $0.03 per word;
            that's $800 - $2,400 for a full length novel! <Iris /> reports are
            much more affordable (<b>by a factor of ~20-30x</b>) and provide a
            wealth of information that line edits don't.
          </Typography>
        </Grid>
        <Grid item={true} xs={12} md={4}>
          <Box
            height={48}
            width={40}
            marginBottom={2}
            px={0}
            borderBottom={`2px solid ${theme.palette.primary.main}`}
            textAlign="center"
          >
            <Timer color="primary" />
          </Box>
          <Typography
            variant="h5"
            gutterBottom={true}
            style={{ fontWeight: 700 }}
          >
            Instant
          </Typography>
          <Typography color="textSecondary">
            While traditional editing typically takes weeks, <Iris />' reports
            are available instantly - usually within less than 5 minutes after
            upload.
          </Typography>
        </Grid>
        <Grid item={true} xs={12} md={4}>
          <Box
            height={48}
            width={40}
            marginBottom={2}
            px={0}
            borderBottom={`2px solid ${theme.palette.primary.main}`}
            textAlign="center"
          >
            <EmojiObjects color="primary" />
          </Box>
          <Typography
            variant="h5"
            gutterBottom={true}
            style={{ fontWeight: 700 }}
          >
            Objective
          </Typography>
          <Typography color="textSecondary">
            Our ML and NLP-based systems are free of subjective, individual
            influences - they have the wisdom of somebody who's read over 2
            million books.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export const Pricing = (props: {
  canExpand: boolean;
  mainPrice: "subscription" | "singleReport";
}) => {
  const { canExpand, mainPrice } = props;
  const theme = useTheme();

  const [isExpanded, setExpanded] = React.useState(!canExpand);
  const [singleReportOption, setSingleReportOption] = React.useState<
    "training" | "privacy"
  >("privacy");
  const [subscriptionOption, setSubscriptionOption] = React.useState<
    "yearly" | "monthly"
  >("yearly");

  const singleReportContent = (
    <CardContent>
      <Typography variant="overline" color="textSecondary">
        <IrisAssessment />
      </Typography>
      <Box display="flex">
        <Box flexGrow={1}>
          <Typography
            variant="h4"
            color="textPrimary"
            style={{ fontWeight: 500 }}
          >
            Single Report
          </Typography>
        </Box>
        <Box flexGrow={0}>
          <Typography
            variant="h4"
            style={{
              color: theme.palette.primary.main,
            }}
          >
            {singleReportOption === "privacy" ? "$69" : "$59"}
          </Typography>
        </Box>
      </Box>
      <Box
        mb={4}
        textAlign="right"
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
      >
        <Typography
          variant="body2"
          color={
            singleReportOption === "training" ? "primary" : "textSecondary"
          }
          style={{ fontWeight: 700 }}
        >
          Allow Training
        </Typography>
        <Switch
          color="primary"
          checked={singleReportOption === "privacy" ? true : false}
          onChange={(_, checked) =>
            setSingleReportOption(checked ? "privacy" : "training")
          }
        />
        <Typography
          variant="body2"
          color={singleReportOption === "privacy" ? "primary" : "textSecondary"}
          style={{ fontWeight: 700 }}
        >
          Full Privacy
        </Typography>
      </Box>
      <Box mb={4}>
        <Typography variant="body1" color="textSecondary">
          One full <Iris /> report for your entire manuscript, including metrics
          and actionable feedback.
        </Typography>
      </Box>
      {isExpanded && (
        <List>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="The full report is available within minutes" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="5 predictive metrics: sales potential, reader rating, genre fit, style fit, manuscript score" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="30+ NLP metrics to compare your manuscript against bestsellers and genre norms" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="Genre analysis across 40 genres" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Link component={RouterLink} to="/business-card">
                    Book business card
                  </Link>{" "}
                  for your manuscript
                </>
              }
            />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="We will get your manuscript in front of a publisher (for scores over 80%)" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <b>
                    {singleReportOption === "privacy"
                      ? "Your manuscript is fully private, Quantifiction Inc. is not allowed to use your data for training."
                      : "Through this discounted rate, you allow Quantifiction Inc. to train models on your content."}
                  </b>
                </>
              }
            />
          </ListItem>
        </List>
      )}
    </CardContent>
  );

  const subscriptionContent = (
    <CardContent>
      <Typography variant="overline" color="textSecondary">
        <IrisEditor />
      </Typography>
      <Box display="flex">
        <Box flexGrow={1}>
          <Typography
            variant="h4"
            color="textPrimary"
            style={{ fontWeight: 500 }}
          >
            Subscription
          </Typography>
        </Box>
        <Box flexGrow={0}>
          <Typography
            variant="h4"
            style={{
              color: theme.palette.primary.main,
            }}
          >
            {subscriptionOption === "yearly" ? "$179" : "$29"}
          </Typography>
        </Box>
      </Box>
      <Box
        mb={4}
        textAlign="right"
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
      >
        <Typography
          variant="body2"
          color={subscriptionOption === "monthly" ? "primary" : "textSecondary"}
          style={{ fontWeight: 700 }}
        >
          Monthly
        </Typography>
        <Switch
          color="primary"
          checked={subscriptionOption === "yearly" ? true : false}
          onChange={(_, checked) =>
            setSubscriptionOption(checked ? "yearly" : "monthly")
          }
        />
        <Typography
          variant="body2"
          color={subscriptionOption === "yearly" ? "primary" : "textSecondary"}
          style={{ fontWeight: 700 }}
        >
          Yearly
        </Typography>
      </Box>
      <Box mb={4}>
        <Typography variant="body1" color="textSecondary">
          Our intelligent writing companion, delivering real-time feedback
          tailored to creative storytelling.
        </Typography>
      </Box>
      {isExpanded && (
        <List>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="Real-time feedback as you type" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="5 predictive metrics: sales potential, reader rating, genre fit, style fit, manuscript score" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="30+ NLP metrics to compare your manuscript against bestsellers and genre norms" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="Genre analysis across 40 genres" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="Editorial suggestions detecting clichés, show don't tell, inconsistencies, ..." />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText primary="Unlimited drafts and edits" />
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <b>
                    {subscriptionOption === "monthly"
                      ? "Real-time editorial feedback for less than $1 per day"
                      : "Real-time editorial feedback for less than $0.5 per day"}
                  </b>
                </>
              }
            />
          </ListItem>
        </List>
      )}
    </CardContent>
  );

  const customContent = (
    <CardContent
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="overline" color="textSecondary">
        <b>
          Beyond <Iris />
        </b>
      </Typography>
      <Box display="flex" mb={4}>
        <Typography
          variant="h4"
          color="textPrimary"
          style={{ fontWeight: 500 }}
        >
          Custom Pricing
        </Typography>
      </Box>
      <Box mb={4} flexGrow={0}>
        <Typography variant="body1" color="textSecondary">
          We offer customized pricing packages for all who frequently use{" "}
          <Iris /> to evaluate a large number of manuscripts.
        </Typography>
      </Box>
      {isExpanded && (
        <>
          <Box flexGrow={1}>
            <List>
              <ListItem disableGutters={true}>
                <ListItemIcon>
                  <CheckCircle color="primary" />
                </ListItemIcon>
                <ListItemText primary="Tailored monthly packages to your needs" />
              </ListItem>
              <ListItem disableGutters={true}>
                <ListItemIcon>
                  <CheckCircle color="primary" />
                </ListItemIcon>
                <ListItemText primary="Progressive discounts" />
              </ListItem>
              <ListItem disableGutters={true}>
                <ListItemIcon>
                  <CheckCircle color="primary" />
                </ListItemIcon>
                <ListItemText primary="Individual training sessions and seminars" />
              </ListItem>
              <ListItem disableGutters={true}>
                <ListItemIcon>
                  <CheckCircle color="primary" />
                </ListItemIcon>
                <ListItemText primary="Custom models and integrations" />
              </ListItem>
            </List>
          </Box>
          <Box flexGrow={0} mb={4}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              href={"mailto:" + INFO_EMAIL}
              fullWidth={true}
              style={{ fontWeight: 700 }}
            >
              Contact us
            </Button>
          </Box>
        </>
      )}
    </CardContent>
  );

  return (
    <Grid container={true} spacing={4}>
      <Grid item={true} xs={12} md={4}>
        <Card
          elevation={1}
          style={{ height: "100%", borderRadius: 8, paddingTop: 16 }}
        >
          {mainPrice === "subscription"
            ? singleReportContent
            : subscriptionContent}
        </Card>
      </Grid>
      <Grid item={true} xs={12} md={4}>
        <Card
          elevation={8}
          style={{ height: "100%", borderRadius: 8, paddingTop: 16 }}
        >
          {mainPrice === "subscription"
            ? subscriptionContent
            : singleReportContent}
        </Card>
      </Grid>
      <Grid item={true} xs={12} md={4}>
        <Card
          elevation={1}
          style={{
            display: "flex",
            height: "100%",
            borderRadius: 8,
            paddingTop: 16,
          }}
        >
          {customContent}
        </Card>
      </Grid>
      {canExpand && !isExpanded && (
        <Grid
          item={true}
          xs={12}
          style={{ display: "flex", marginTop: 16, justifyContent: "center" }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => setExpanded(true)}
          >
            Show More
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

const GetStarted = () => {
  return (
    <Box mt={8}>
      <PageHeader
        overline="Get Started"
        title={
          <>
            Get started with <Iris /> today
          </>
        }
        subtitle={
          <>
            Join the publishing revolution and be an early adopter of <Iris />.
          </>
        }
        align="center"
      />
      <Box display="flex" justifyContent="center">
        <SignupButton place="pricing-page" />
      </Box>
    </Box>
  );
};

export default PricingPage;



// WEBPACK FOOTER //
// ./src/pages/PricingPage.tsx